import { ArrowLeft, Ban, Check, Copy, Link2, Share2, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import type { Friend, PartenaireClasse } from '../../lib/friends.ts';
import {
    createInvite, getPodiumPartenaires, inviteUrl, listFriends, listInvites,
    removeFriend, revokeInvites,
} from '../../lib/friends.ts';
import { PodiumPartenaires } from './PodiumPartenaires.tsx';

/* Ajouter un ami, sans annuaire.
 *
 * Il n'y a volontairement aucune recherche par nom ni par adresse. Une
 * recherche transformerait la base de comptes en répertoire consultable, et
 * permettrait de vérifier qu'une adresse donnée est bien inscrite ici — ce
 * qu'aucun joueur n'a demandé et que personne ne peut retirer une fois offert.
 *
 * Le lien, lui, ne se trouve pas : il se donne. Il part par le canal que le
 * joueur utilise déjà pour parler à ses partenaires — messagerie, SMS — via le
 * partage natif du téléphone quand il existe.
 */

export function Friends() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [friends, setFriends] = useState<Friend[]>([]);
    const [classement, setClassement] = useState<PartenaireClasse[]>([]);
    const [pending, setPending] = useState<{ createdAt: string; expiresAt: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [link, setLink] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmRemove, setConfirmRemove] = useState<Friend | null>(null);

    const reload = useCallback(async () => {
        try {
            const [f, i] = await Promise.all([listFriends(), listInvites()]);
            setFriends(f); setPending(i);
            /* Le classement à part, et sans faire échouer la page.
               Il ne sert à rien d'empêcher quelqu'un d'ajouter un partenaire
               parce que le calcul de ses victoires a échoué : la carte
               disparaît, le reste tient. */
            void getPodiumPartenaires().then(setClassement).catch(() => setClassement([]));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    const makeLink = async (): Promise<void> => {
        setBusy(true); setError(null); setCopied(false);
        try {
            const { token } = await createInvite();
            const url = inviteUrl(token);
            setLink(url);

            // Le partage natif ouvre la liste des applications du téléphone :
            // c'est le geste attendu, et il évite le détour par le presse-
            // papiers. Absent sur ordinateur, d'où la copie en repli.
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: 'Golf',
                        text: 'Ajoute-moi comme partenaire de putting sur Golf',
                        url,
                    });
                    return;
                } catch {
                    // Partage annulé : le lien reste affiché, à copier.
                }
            }
            await copy(url);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Création du lien impossible');
        } finally {
            setBusy(false);
        }
    };

    const copy = async (url: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // Presse-papiers refusé (contexte non sécurisé, permission) : le
            // lien reste sélectionnable à l'écran, ce qui suffit.
        }
    };

    const revokeAll = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            await revokeInvites();
            setLink(null);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Annulation impossible');
        } finally { setBusy(false); }
    };

    const remove = async (f: Friend): Promise<void> => {
        setBusy(true);
        try {
            await removeFriend(f.id);
            setConfirmRemove(null);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5 py-2">
            <header>
                {/* Retour à l'écran précédent, et non à une adresse fixe : on
                    arrive ici depuis le tiroir comme depuis le putting, et une
                    destination écrite en dur serait fausse une fois sur deux. */}
                <button type="button" onClick={() => void navigate(-1)}
                    className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Retour
                </button>
                <h1 className="text-2xl font-bold tracking-tight">Mes partenaires</h1>
                {/* « à une partie de putting » ne suffisait plus : on invite
                    ses partenaires à un parcours depuis longtemps, et le
                    podium juste dessous les classe désormais sur les deux. */}
                <p className="mt-1 text-sm opacity-60">
                    Les joueurs que vous pouvez inviter à un parcours ou à une partie
                    de putting.
                </p>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* Le podium AVANT le bouton d'invitation.
                C'est ce qu'on vient voir quand on revient sur cette page :
                inviter un partenaire se fait une fois, regarder qui mène se
                refait après chaque partie. La carte ne paraît que s'il y a
                quelque chose à classer — une estrade vide en haut d'un écran
                de début ne promet rien de bon. */}
            {classement.length > 0 && (
                <PodiumPartenaires joueurs={classement} moiId={user?.id} />
            )}

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <UserPlus size={18} className="text-primary" /> Ajouter un partenaire
                </h2>
                <p className="mt-1 text-xs opacity-60">
                    Envoyez-lui un lien. Il est valable 7 jours et ne fonctionne qu'une
                    seule fois — la personne qui l'ouvre est la seule à être ajoutée.
                </p>

                <button type="button" disabled={busy} onClick={() => void makeLink()}
                    className="btn btn-primary btn-block mt-3 gap-2 tap-target">
                    <Share2 size={18} /> Créer un lien d'invitation
                </button>

                {link && (
                    <div className="mt-3 rounded-field bg-base-100 p-3">
                        <div className="flex items-center gap-2 text-xs opacity-60">
                            <Link2 size={14} /> Lien à partager
                        </div>
                        {/* break-all : une URL sans espaces déborde sinon de
                            l'écran, et le lien devient illisible donc inutile. */}
                        <p className="mt-1 break-all font-mono text-xs">{link}</p>
                        <button type="button" onClick={() => void copy(link)}
                            className="btn btn-ghost btn-sm mt-2 gap-2">
                            {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                            {copied ? 'Copié' : 'Copier'}
                        </button>
                    </div>
                )}

                {/* Les liens encore dehors.
                    On ne peut pas les distinguer les uns des autres — ils sont
                    indiscernables une fois envoyés, et le jeton n'est plus
                    lisible — mais savoir qu'il en court encore, et pouvoir tout
                    couper, suffit au seul cas qui compte : « je viens de le
                    coller au mauvais endroit ». */}
                {pending.length > 0 && (
                    <div className="mt-3 border-t border-base-300 pt-3">
                        <p className="text-xs opacity-60">
                            {pending.length} lien{pending.length > 1 ? 's' : ''} en attente,
                            {' '}le plus ancien valable jusqu'au{' '}
                            {new Date(pending[pending.length - 1]!.expiresAt)
                                .toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}.
                        </p>
                        <button type="button" disabled={busy}
                            onClick={() => void revokeAll()}
                            className="btn btn-ghost btn-xs mt-1 gap-2 text-error">
                            <Ban size={14} /> Annuler {pending.length > 1 ? 'ces liens' : 'ce lien'}
                        </button>
                    </div>
                )}
            </section>

            {loading ? (
                <div className="flex justify-center py-10">
                    <span className="loading loading-ring loading-lg text-primary" />
                </div>
            ) : friends.length === 0 ? (
                <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                    Aucun partenaire pour l'instant. Partagez un lien pour en ajouter un.
                </p>
            ) : (
                <section className="space-y-2">
                    <h2 className="text-sm font-semibold">
                        {friends.length} partenaire{friends.length > 1 ? 's' : ''}
                    </h2>
                    {friends.map((f) => (
                        <div key={f.id} className="flex items-center gap-3 rounded-box bg-base-200 p-3">
                            {/* Toute la ligne mene au profil : c'est la qu'on
                                voit ses derniers parcours et qu'on l'invite. */}
                            <Link to={`/amis/${f.id}`}
                                className="flex min-w-0 flex-1 items-center gap-3">
                                <Avatar name={f.displayName} email={f.email}
                                    avatarUrl={f.avatarUrl} size={40}
                                    handicap={f.handicapIndex} />
                            {/* L'index ne s'écrit plus à côté du nom : il est sur
                                l'avatar, comme partout ailleurs. Deux fois le
                                même chiffre sur une ligne de trois éléments,
                                c'est un chiffre de trop à démêler dehors. */}
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold">{f.displayName}</div>
                                <div className="text-xs opacity-60">
                                    {f.gamesTogether > 0
                                        ? `${f.gamesTogether} partie${f.gamesTogether > 1 ? 's' : ''} ensemble`
                                        : 'Jamais joué ensemble'}
                                </div>
                            </div>
                            </Link>
                            <button type="button" aria-label={`Retirer ${f.displayName}`}
                                onClick={() => setConfirmRemove(f)}
                                className="btn btn-ghost btn-square btn-sm text-error">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </section>
            )}

            {confirmRemove && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">
                            Retirer {confirmRemove.displayName} ?
                        </h2>
                        <p className="mt-2 text-sm opacity-80">
                            Vous ne pourrez plus vous inviter mutuellement à une partie.
                        </p>
                        {/* Dire ce qui NE disparaît pas vaut mieux que de laisser
                            croire qu'on efface un passé commun. */}
                        <p className="mt-2 text-xs opacity-60">
                            Les parties déjà jouées ensemble et vos statistiques restent
                            intactes. Vous pourrez vous réajouter par un nouveau lien.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void remove(confirmRemove)}
                                className="btn btn-error btn-block tap-target">
                                Retirer
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmRemove(null)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
